// src/pages/lab/protocols/components/stepEditingComponents/StepMethodsEditor.tsx
import React, { useState } from "react";
import { Table, Button, Drawer, Input, InputNumber, Space, Modal, Tooltip, message } from "antd";
import { PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";

import { ProtocolStepMethod } from "../../../../../../types/lab/ProtocolTypes";

const { TextArea } = Input;
const { confirm } = Modal;

interface StepMethodsEditorProps {
	methods: ProtocolStepMethod[];
	onMethodsChange: (methods: ProtocolStepMethod[]) => void;
}

const StepMethodsEditor: React.FC<StepMethodsEditorProps> = ({ methods, onMethodsChange }) => {
	const [editingMethod, setEditingMethod] = useState<Partial<ProtocolStepMethod>>({ name: "", description: "", step_order_number: 1 });
	const [methodDrawerVisible, setMethodDrawerVisible] = useState(false);

	const handleAddMethod = () => {
		const nextOrderNumber = methods.length > 0 ? Math.max(...methods.map((m) => m.step_order_number)) + 1 : 1;
		setEditingMethod({ name: "", description: "", step_order_number: nextOrderNumber });
		setMethodDrawerVisible(true);
	};

	const handleEditMethod = (method: ProtocolStepMethod) => {
		setEditingMethod({ ...method });
		setMethodDrawerVisible(true);
	};

	const handleDuplicateMethod = (method: ProtocolStepMethod) => {
		const nextOrderNumber = methods.length > 0 ? Math.max(...methods.map((m) => m.step_order_number)) + 1 : 1;
		setEditingMethod({
			name: `${method.name} (Copy)`,
			description: method.description,
			step_order_number: nextOrderNumber,
		});
		setMethodDrawerVisible(true);
	};

	const handleDeleteMethod = (method: ProtocolStepMethod) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete method "${method.name}"?`,
			onOk: () => {
				const updatedMethods = methods.filter((m) => m.step_order_number !== method.step_order_number).map((m, index) => ({ ...m, step_order_number: index + 1 }));
				onMethodsChange(updatedMethods);
			},
		});
	};

	const handleMoveMethod = (methodOrderNumber: number, direction: "up" | "down") => {
		const currentIndex = methods.findIndex((m) => m.step_order_number === methodOrderNumber);
		const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

		if (targetIndex < 0 || targetIndex >= methods.length) return;

		const updatedMethods = [...methods];
		const temp = updatedMethods[currentIndex];
		updatedMethods[currentIndex] = updatedMethods[targetIndex];
		updatedMethods[targetIndex] = temp;

		updatedMethods.forEach((method, index) => {
			method.step_order_number = index + 1;
		});

		onMethodsChange(updatedMethods);
	};

	const handleSaveMethod = () => {
		if (!editingMethod.name?.trim()) {
			message.error("Method name is required");
			return;
		}

		const updatedMethods = [...methods];
		const existingIndex = updatedMethods.findIndex((m) => m.step_order_number === editingMethod.step_order_number && m.name === editingMethod.name);

		const now = new Date().toISOString();
		const methodToSave = {
			...editingMethod,
			created_at: editingMethod.created_at || now,
			updated_at: now,
		} as ProtocolStepMethod;

		if (existingIndex >= 0) {
			updatedMethods[existingIndex] = methodToSave;
		} else {
			updatedMethods.push(methodToSave);
			updatedMethods.sort((a, b) => a.step_order_number - b.step_order_number);
		}

		onMethodsChange(updatedMethods);
		setMethodDrawerVisible(false);
		setEditingMethod({ name: "", description: "", step_order_number: 1 });
	};

	const sortedMethods = [...methods].sort((a, b) => a.step_order_number - b.step_order_number);

	const columns = [
		{
			title: "Order",
			key: "order",
			width: 80,
			render: (record: ProtocolStepMethod) => (
				<Space size={4}>
					<span style={{ fontSize: "11px", fontWeight: 500 }}>{record.step_order_number}</span>
					<Space direction="vertical" size={0}>
						<Button type="text" size="small" icon={<ArrowUpOutlined />} onClick={() => handleMoveMethod(record.step_order_number, "up")} disabled={record.step_order_number === 1} style={{ padding: "0 4px", height: "16px" }} />
						<Button
							type="text"
							size="small"
							icon={<ArrowDownOutlined />}
							onClick={() => handleMoveMethod(record.step_order_number, "down")}
							disabled={record.step_order_number === methods.length}
							style={{ padding: "0 4px", height: "16px" }}
						/>
					</Space>
				</Space>
			),
		},
		{
			title: "Method Information",
			key: "info",
			render: (record: ProtocolStepMethod) => (
				<div>
					<div style={{ fontWeight: 500, fontSize: "12px", marginBottom: 4 }}>{record.name}</div>
					{record.description && <div style={{ fontSize: "11px", color: "var(--text-color-secondary)", maxWidth: 250 }}>{record.description}</div>}
				</div>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			render: (record: ProtocolStepMethod) => (
				<Space size={4}>
					<Tooltip title="Edit">
						<Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditMethod(record)} />
					</Tooltip>
					<Tooltip title="Duplicate">
						<Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleDuplicateMethod(record)} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteMethod(record)} />
					</Tooltip>
				</Space>
			),
		},
	];

	return (
		<div>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
				<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>Define the exact procedural steps that technicians must follow</p>
				<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddMethod}>
					Add Step
				</Button>
			</div>

			{methods.length > 0 ? (
				<Table columns={columns} dataSource={sortedMethods} rowKey="step_order_number" pagination={false} size="small" />
			) : (
				<div style={{ textAlign: "center", padding: "48px 24px", border: "2px dashed var(--border-color)", borderRadius: 8 }}>
					<div style={{ fontSize: "48px", marginBottom: 16 }}>📝</div>
					<h6 style={{ fontSize: "13px", margin: "0 0 4px 0" }}>No Methods Defined</h6>
					<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", maxWidth: 320, margin: "0 auto 16px" }}>Break down this protocol step into detailed procedural methods</p>
					<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddMethod}>
						Add First Step
					</Button>
				</div>
			)}

			<Drawer
				title={editingMethod.name ? "Edit Method Step" : "Create New Method Step"}
				open={methodDrawerVisible}
				onClose={() => setMethodDrawerVisible(false)}
				width={500}
				footer={
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<div style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>
							Step {editingMethod.step_order_number} • {editingMethod.name || "New Method Step"}
						</div>
						<Space size="small">
							<Button icon={<CloseOutlined />} onClick={() => setMethodDrawerVisible(false)} size="small">
								Cancel
							</Button>
							<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveMethod} disabled={!editingMethod.name?.trim()} size="small">
								Save Method
							</Button>
						</Space>
					</div>
				}
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Step Name *</label>
							<Input value={editingMethod.name || ""} onChange={(e) => setEditingMethod({ ...editingMethod, name: e.target.value })} placeholder="e.g., Centrifuge samples" size="small" />
							<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Brief, action-oriented description</div>
						</div>

						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Order *</label>
							<InputNumber value={editingMethod.step_order_number || 1} onChange={(value) => setEditingMethod({ ...editingMethod, step_order_number: value || 1 })} min={1} max={100} size="small" style={{ width: "100%" }} />
						</div>
					</div>

					<div>
						<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Detailed Instructions</label>
						<TextArea value={editingMethod.description || ""} onChange={(e) => setEditingMethod({ ...editingMethod, description: e.target.value })} placeholder="Provide detailed step-by-step instructions..." rows={4} size="small" />
						<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Include specific parameters, timing, and safety considerations</div>
					</div>
				</Space>
			</Drawer>
		</div>
	);
};

export default StepMethodsEditor;
